/**
 * PropsFacet Component
 *
 * Hierarchical facet component for prop taxonomy filtering with:
 * - Two-level hierarchy: Categories → Specific Props
 * - Search box for filtering props/categories
 * - AND/OR toggle for multi-select logic
 * - Moving Heads compatibility checkbox
 * - Visual display with counts
 */

import React, { useState, useMemo } from 'react';
import { useRefinementList, useToggleRefinement } from 'react-instantsearch';
import { ChevronDown, ChevronRight, Search as SearchIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface PropsFacetProps {
  expanded?: boolean;
}

export const PropsFacet: React.FC<PropsFacetProps> = ({ expanded = true }) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [conjunctionMode, setConjunctionMode] = useState<'AND' | 'OR'>('OR');

  // Category facet (props_category_slugs)
  const categoryFacet = useRefinementList({
    attribute: 'props_category_slugs',
    limit: 100,
    operator: conjunctionMode === 'AND' ? 'and' : 'or',
  });

  // Specific props facet (props_used_names)
  const propsFacet = useRefinementList({
    attribute: 'props_used_names',
    limit: 200,
    operator: conjunctionMode === 'AND' ? 'and' : 'or',
  });

  // Moving heads toggle
  const movingHeadsToggle = useToggleRefinement({
    attribute: 'has_moving_heads',
    on: true,
  });

  // Toggle category expansion
  const toggleCategory = (categorySlug: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categorySlug)) {
      newExpanded.delete(categorySlug);
    } else {
      newExpanded.add(categorySlug);
    }
    setExpandedCategories(newExpanded);
  };

  // Filter items by search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery) return categoryFacet.items;
    return categoryFacet.items.filter(item =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categoryFacet.items, searchQuery]);

  const filteredProps = useMemo(() => {
    if (!searchQuery) return propsFacet.items;
    return propsFacet.items.filter(item =>
      item.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [propsFacet.items, searchQuery]);

  // Group props by category (based on naming conventions)
  // This is a simplified approach - in production you might want to fetch category mappings
  const groupPropsByCategory = (props: typeof propsFacet.items) => {
    const grouped = new Map<string, typeof propsFacet.items>();

    props.forEach(prop => {
      // Simple heuristic: extract category from prop name
      // E.g., "Ice Queen Snowflake" → "snowflakes"
      // In production, this should use actual database relationships
      const lowerName = prop.label.toLowerCase();

      let category = 'other';
      if (lowerName.includes('snowflake')) category = 'snowflakes';
      else if (lowerName.includes('tree') || lowerName.includes('mega')) category = 'trees';
      else if (lowerName.includes('arch')) category = 'arches';
      else if (lowerName.includes('wreath')) category = 'wreaths';
      else if (lowerName.includes('matrix') || lowerName.includes('window')) category = 'matrix-effects';
      else if (lowerName.includes('spinner')) category = 'spinners';
      else if (lowerName.includes('moving head')) category = 'moving-heads';
      else if (lowerName.includes('inflatable')) category = 'inflatables';
      else if (lowerName.includes('candy cane')) category = 'candy-canes';
      else if (lowerName.includes('star')) category = 'stars-toppers';
      else if (lowerName.includes('icicle')) category = 'icicles';

      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(prop);
    });

    return grouped;
  };

  const groupedProps = useMemo(() =>
    groupPropsByCategory(filteredProps),
    [filteredProps]
  );

  // Count total selections
  const totalSelections =
    categoryFacet.items.filter(item => item.isRefined).length +
    propsFacet.items.filter(item => item.isRefined).length +
    (movingHeadsToggle.value.isRefined ? 1 : 0);

  // Hide if no items and not searchable
  if (categoryFacet.items.length === 0 && propsFacet.items.length === 0) {
    return null;
  }

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
      <CollapsibleTrigger asChild>
        <Button variant="ghost" className="w-full justify-between p-2 h-auto">
          <div className="flex items-center gap-2">
            <span className="font-medium">Props</span>
            {totalSelections > 0 && (
              <Badge variant="secondary" className="ml-1">
                {totalSelections}
              </Badge>
            )}
          </div>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform",
              isExpanded && "rotate-180"
            )}
          />
        </Button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-3 p-2">
        {/* Search Box */}
        <div className="relative">
          <SearchIcon className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search props..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-8"
          />
        </div>

        {/* AND/OR Toggle */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Match:</span>
          <Select
            value={conjunctionMode}
            onValueChange={(value) => setConjunctionMode(value as 'AND' | 'OR')}
          >
            <SelectTrigger className="w-24 h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="OR">Any (OR)</SelectItem>
              <SelectItem value="AND">All (AND)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Moving Heads Compatibility Filter */}
        <div className="flex items-center space-x-2 py-1 px-2 bg-muted/30 rounded">
          <Checkbox
            id="has-moving-heads"
            checked={movingHeadsToggle.value.isRefined}
            onCheckedChange={() => movingHeadsToggle.refine({ isRefined: !movingHeadsToggle.value.isRefined })}
          />
          <label
            htmlFor="has-moving-heads"
            className="flex-1 text-sm cursor-pointer font-medium"
          >
            Moving Heads Compatible
          </label>
        </div>

        <ScrollArea className="max-h-96">
          <div className="space-y-2">
            {/* Category-level filters */}
            {filteredCategories.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
                  Categories
                </div>
                {filteredCategories.map((category) => {
                  const categorySlug = category.value;
                  const isExpanded = expandedCategories.has(categorySlug);
                  const categoryProps = groupedProps.get(categorySlug) || [];

                  return (
                    <div key={categorySlug} className="space-y-1">
                      {/* Category checkbox */}
                      <div className="flex items-center space-x-2 py-1 px-2 hover:bg-muted/50 rounded">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                          onClick={() => toggleCategory(categorySlug)}
                        >
                          {categoryProps.length > 0 ? (
                            isExpanded ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )
                          ) : (
                            <div className="w-3" />
                          )}
                        </Button>
                        <Checkbox
                          id={`category-${categorySlug}`}
                          checked={category.isRefined}
                          onCheckedChange={() => categoryFacet.refine(category.value)}
                        />
                        <label
                          htmlFor={`category-${categorySlug}`}
                          className="flex-1 text-sm cursor-pointer flex justify-between"
                        >
                          <span className="truncate font-medium">{category.label}</span>
                          <span className="text-muted-foreground ml-2">({category.count})</span>
                        </label>
                      </div>

                      {/* Specific props under category */}
                      {isExpanded && categoryProps.length > 0 && (
                        <div className="ml-6 space-y-1 border-l-2 border-muted pl-2">
                          {categoryProps.map((prop) => (
                            <div key={prop.value} className="flex items-center space-x-2 py-1">
                              <Checkbox
                                id={`prop-${prop.value}`}
                                checked={prop.isRefined}
                                onCheckedChange={() => propsFacet.refine(prop.value)}
                              />
                              <label
                                htmlFor={`prop-${prop.value}`}
                                className="flex-1 text-sm cursor-pointer flex justify-between"
                              >
                                <span className="truncate text-xs">{prop.label}</span>
                                <span className="text-muted-foreground ml-2 text-xs">({prop.count})</span>
                              </label>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Ungrouped specific props */}
            {groupedProps.get('other') && groupedProps.get('other')!.length > 0 && (
              <div className="space-y-1 mt-3">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide px-2 py-1">
                  Other Props
                </div>
                {groupedProps.get('other')!.map((prop) => (
                  <div key={prop.value} className="flex items-center space-x-2 py-1 px-2">
                    <Checkbox
                      id={`prop-${prop.value}`}
                      checked={prop.isRefined}
                      onCheckedChange={() => propsFacet.refine(prop.value)}
                    />
                    <label
                      htmlFor={`prop-${prop.value}`}
                      className="flex-1 text-sm cursor-pointer flex justify-between"
                    >
                      <span className="truncate">{prop.label}</span>
                      <span className="text-muted-foreground ml-2">({prop.count})</span>
                    </label>
                  </div>
                ))}
              </div>
            )}

            {/* No results message */}
            {filteredCategories.length === 0 && filteredProps.length === 0 && searchQuery && (
              <div className="text-sm text-muted-foreground py-2 px-2 text-center">
                No props found matching "{searchQuery}"
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Show More buttons */}
        <div className="flex gap-2">
          {categoryFacet.canToggleShowMore && (
            <Button
              variant="ghost"
              size="sm"
              onClick={categoryFacet.toggleShowMore}
              className="flex-1"
            >
              {categoryFacet.isShowingMore ? 'Show Less Categories' : 'Show More Categories'}
            </Button>
          )}
          {propsFacet.canToggleShowMore && (
            <Button
              variant="ghost"
              size="sm"
              onClick={propsFacet.toggleShowMore}
              className="flex-1"
            >
              {propsFacet.isShowingMore ? 'Show Less Props' : 'Show More Props'}
            </Button>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
